/**
 * Workspaces panel — Iter 3 (Enterprise Projects Phase 1).
 *
 * Renders the workspace strip attached to an engagement. Data-driven
 * from the catalogue (`GET /catalogue/workspace/list`) and the active
 * project workspaces (`GET /engagement/{id}/workspace/list`).
 *
 * A workspace card exposes:
 *   • Activate button (if not yet activated on this engagement).
 *   • Members button (opens the invite dialog).
 *   • A read-only member list; the strip surfaces
 *     `403 not_a_workspace_member` gracefully.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    LayoutDashboard, Briefcase, Receipt, LifeBuoy, ShieldCheck,
    AlertTriangle, Flag, Folder, MessageSquare, Users, Mail,
    BarChart3, Hash, Plus,
} from "lucide-react";

import { api, formatApiError } from "../../../lib/api";
import { formatDate, PageLoading } from "./ui";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import WorkspaceInviteDialog from "./WorkspaceInviteDialog";


const ICON_MAP = {
    "layout-dashboard": LayoutDashboard,
    briefcase: Briefcase,
    receipt: Receipt,
    "life-buoy": LifeBuoy,
    "shield-check": ShieldCheck,
    "alert-triangle": AlertTriangle,
    flag: Flag,
    folder: Folder,
    "message-square": MessageSquare,
    users: Users,
    mail: Mail,
    "bar-chart-3": BarChart3,
    hash: Hash,
};


export default function WorkspacesPanel({ engagementId, canManage }) {
    const [catalogue, setCatalogue] = useState([]);
    const [activated, setActivated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCode, setActiveCode] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteCode, setInviteCode] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, a] = await Promise.all([
                api.get("/projects/catalogue/workspace/list"),
                api.get(`/projects/engagement/${engagementId}/workspace/list`),
            ]);
            setCatalogue(c.data?.items || []);
            setActivated(a.data?.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [engagementId]);

    useEffect(() => { load(); }, [load]);

    const activatedByCode = useMemo(() => {
        const m = {};
        for (const it of activated) m[it.workspace.code] = it;
        return m;
    }, [activated]);

    const activate = async (code) => {
        try {
            await api.post(`/projects/engagement/${engagementId}/workspace/${code}/activate`);
            toast.success(`Workspace "${code}" activated.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openInvite = (code) => {
        setInviteCode(code);
        setInviteOpen(true);
    };

    if (loading) {
        return <PageLoading label="Loading workspaces…" testId="workspaces-loading" />;
    }

    return (
        <section data-testid="workspaces-panel" className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Workspaces</h2>
                <span className="text-xs text-slate-500">
                    {activated.length} active of {catalogue.length} available
                </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {catalogue.map((ws) => {
                    const Icon = ICON_MAP[ws.icon] || Hash;
                    const inst = activatedByCode[ws.code];
                    const isActive = !!inst;
                    const canRead = inst?.can_read ?? false;
                    return (
                        <div
                            key={ws.code}
                            data-testid={`workspace-card-${ws.code}`}
                            className={
                                "border rounded-md p-3 flex flex-col gap-2 " +
                                (isActive
                                    ? "border-emerald-300 bg-emerald-50/30"
                                    : "border-slate-200 bg-slate-50/50")
                            }
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4 text-slate-600" />
                                    <span className="text-sm font-medium text-slate-900">{ws.name}</span>
                                    {ws.is_shared && (
                                        <span
                                            data-testid={`workspace-shared-badge-${ws.code}`}
                                            className="ml-1 px-1.5 py-0.5 text-[10px] uppercase rounded bg-sky-100 text-sky-700 border border-sky-200"
                                        >
                                            Shared
                                        </span>
                                    )}
                                </div>
                                {isActive ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                                        Active
                                    </span>
                                ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">
                                        Inactive
                                    </span>
                                )}
                            </div>
                            {ws.description && (
                                <p className="text-xs text-slate-500 line-clamp-2">{ws.description}</p>
                            )}
                            <div className="mt-auto flex items-center gap-2 pt-1">
                                {!isActive && canManage && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        data-testid={`workspace-activate-btn-${ws.code}`}
                                        onClick={() => activate(ws.code)}
                                    >
                                        <Plus className="w-3.5 h-3.5 mr-1" />
                                        Activate
                                    </Button>
                                )}
                                {isActive && (
                                    <>
                                        <Button
                                            size="sm"
                                            variant={activeCode === ws.code ? "default" : "outline"}
                                            data-testid={`workspace-open-btn-${ws.code}`}
                                            onClick={() => setActiveCode(activeCode === ws.code ? null : ws.code)}
                                            disabled={!canRead && !canManage}
                                            title={
                                                canRead || canManage
                                                    ? "Show member list"
                                                    : "You are not a member of this workspace"
                                            }
                                        >
                                            <Users className="w-3.5 h-3.5 mr-1" />
                                            Members
                                        </Button>
                                        {canManage && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                data-testid={`workspace-invite-btn-${ws.code}`}
                                                onClick={() => openInvite(ws.code)}
                                            >
                                                <Plus className="w-3.5 h-3.5 mr-1" />
                                                Invite
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {activeCode && (
                <MemberListSection
                    engagementId={engagementId}
                    code={activeCode}
                    canManage={canManage}
                    onChanged={load}
                />
            )}

            <WorkspaceInviteDialog
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onInvited={() => {
                    setInviteOpen(false);
                    load();
                }}
                engagementId={engagementId}
                workspaceCode={inviteCode}
            />
        </section>
    );
}


function MemberListSection({ engagementId, code, canManage, onChanged }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const r = await api.get(`/projects/engagement/${engagementId}/workspace/${code}/member/list`);
            setRows(r.data?.items || []);
        } catch (err) {
            setRows(null);
            setError(formatApiError(err));
        }
    }, [engagementId, code]);

    useEffect(() => { load(); }, [load]);

    const revoke = async (row) => {
        const reason = window.prompt(
            "Reason for removing this member? (required, min 3 chars)",
            "no longer required on this workspace",
        );
        if (!reason || reason.trim().length < 3) return;
        try {
            await api.delete(
                `/projects/engagement/${engagementId}/workspace/${code}/member/${row.user_id}`,
                { data: { reason: reason.trim() } },
            );
            toast.success("Member removed.");
            load();
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (error) {
        return (
            <div
                data-testid={`workspace-members-error-${code}`}
                className="border border-rose-200 bg-rose-50/50 text-rose-700 rounded-md p-3 text-sm"
            >
                {error}
            </div>
        );
    }
    if (rows === null) {
        return <div className="text-sm text-slate-500 flex items-center gap-2" data-testid={`workspace-members-loading-${code}`}>Loading members…</div>;
    }
    return (
        <div
            data-testid={`workspace-members-panel-${code}`}
            className="border border-slate-200 rounded-md"
        >
            <div className="px-3 py-2 bg-slate-50 text-xs uppercase tracking-wider text-slate-500 flex justify-between">
                <span>{code} · members</span>
                <span data-testid={`workspace-members-count-${code}`}>{rows.length}</span>
            </div>
            {rows.length === 0 ? (
                <div className="p-3 text-sm text-slate-500">No members yet.</div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {rows.map((m) => (
                        <li
                            key={m.id}
                            data-testid={`workspace-member-row-${m.id}`}
                            className="px-3 py-2 flex items-center justify-between text-sm"
                        >
                            <div>
                                <div className="font-medium text-slate-900">
                                    {m.user_snapshot?.name || m.user_id}
                                </div>
                                <div className="text-xs text-slate-500">
                                    {m.role_label} · granted {formatDate(m.granted_at)}
                                </div>
                            </div>
                            {canManage && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid={`workspace-member-revoke-btn-${m.id}`}
                                    onClick={() => revoke(m)}
                                >
                                    Remove
                                </Button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
